/**
 * it-guide-post.js — the styling guide article.
 *
 * Builds the "In this guide" list from the body's chapter headings (the
 * `.guide-look` sections first, then any plain h2 after them), keeps the
 * current chapter marked as the reader scrolls, and turns the copy-link
 * button into a real copy with a toast. Without this module the page is
 * complete: the list is simply empty and the share links are plain links.
 */
class ItGuidePost extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-body]');
    this.toc = this.querySelector('[data-toc]');
    this.toast = this.querySelector('[data-toast]');
    this.buildToc();
    this.bindCopy();
    this.bindShare();
  }

  disconnectedCallback() {
    this.spy?.disconnect();
  }

  buildToc() {
    if (!this.body || !this.toc) return;
    const entries = [];
    const looks = this.body.querySelectorAll('.guide-look');
    looks.forEach((look, i) => {
      const h = look.querySelector('h2, h3');
      if (!h) return;
      if (!look.id) look.id = `look-${String(i + 1).padStart(2, '0')}`;
      const num = look.querySelector('.guide-look__num')?.textContent.trim() || `Look ${String(i + 1).padStart(2, '0')}`;
      entries.push({ id: look.id, label: h.textContent.trim(), kicker: num, target: look });
    });
    this.body.querySelectorAll(':scope > h2').forEach((h, i) => {
      if (!h.id) h.id = `section-${i + 1}`;
      entries.push({ id: h.id, label: h.textContent.trim(), kicker: '', target: h });
    });
    if (!entries.length) {
      this.toc.closest('.it-gp__toc')?.setAttribute('hidden', '');
      return;
    }
    const frag = document.createDocumentFragment();
    entries.forEach((e) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${e.id}`;
      if (e.kicker) {
        const small = document.createElement('small');
        small.textContent = e.kicker;
        a.appendChild(small);
      }
      a.appendChild(document.createTextNode(e.label));
      a.addEventListener('click', (ev) => {
        ev.preventDefault();
        e.target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', `#${e.id}`);
      });
      li.appendChild(a);
      frag.appendChild(li);
      e.link = a;
    });
    this.toc.appendChild(frag);

    if (!('IntersectionObserver' in window)) return;
    const links = new Map(entries.map((e) => [e.target, e.link]));
    const visible = new Set();
    const setOn = () => {
      let pick = null;
      for (const e of entries) {
        if (visible.has(e.target)) { pick = e.link; break; }
      }
      if (!pick) return;
      entries.forEach((e) => e.link.classList.toggle('is-on', e.link === pick));
    };
    this.spy = new IntersectionObserver((recs) => {
      recs.forEach((r) => (r.isIntersecting ? visible.add(r.target) : visible.delete(r.target)));
      setOn();
    }, { rootMargin: '-20% 0px -60% 0px', threshold: 0 });
    links.forEach((_, target) => this.spy.observe(target));
    entries[0].link.classList.add('is-on');
  }

  bindCopy() {
    this.querySelectorAll('[data-copy]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const url = location.href.split('#')[0];
        try {
          await navigator.clipboard.writeText(url);
          this.say('Link copied');
        } catch {
          this.say('Copy failed');
        }
      });
    });
  }

  bindShare() {
    this.querySelectorAll('a[data-net]').forEach((a) => {
      a.addEventListener('click', (ev) => {
        if (ev.metaKey || ev.ctrlKey) return;
        const w = 640, h = 520;
        const left = (screen.width - w) / 2, top = (screen.height - h) / 2;
        const win = window.open(a.href, 'share', `width=${w},height=${h},left=${left},top=${top},noopener`);
        if (win) ev.preventDefault();
      });
    });
  }

  say(text) {
    if (!this.toast) return;
    this.toast.textContent = text;
    this.toast.classList.add('is-on');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast.classList.remove('is-on'), 1800);
  }
}

if (!customElements.get('it-guide-post')) customElements.define('it-guide-post', ItGuidePost);
